import { GrabToPan } from '@go-modules/services/grab-to-pan/index';
import * as angular from 'angular';
import template from './go-pdf.directive.html';

/* @ngInject */
export function goPdfPage ($q, $timeout, goPdfLoader, EventChannel) {
	const RENDERING_THRESHOLD = 300;

	const isolateScope = {
		doc: '=?',
		onLoad: '&',
		onPageRender: '&',
		scale: '=?',
		url: '@'
	};

	const setCanvasDimensions = function (canvas, w, h) {
		canvas.width = Math.floor(w);
		canvas.height = Math.floor(h);
		return canvas;
	};

	const setElementDimensions = function (element, w, h) {
		element.style.width = Math.floor(w) + 'px';
		element.style.height = Math.floor(h) + 'px';
	};

	function link (scope, elem, attr, goPdfPreviewCtrl) {
		const channel = new EventChannel();
		const pageContentElement = elem[0].querySelector('.go-pdf-page-content') as HTMLElement;
		const textLayerElement = elem[0].querySelector('.go-pdf-text-layer') as HTMLDivElement;
		const annotationLayerElement = elem[0].querySelector('.go-pdf-annotation-layer') as HTMLDivElement;
		const pdfJsPromise = goPdfLoader.getPdfJs().catch(() => null);
		let latestRenderToken = 0;

		if (!attr.url && !scope.doc) {
			throw new Error('must provide a pdf path or loaded document');
		}

		let canvasElement;
		let originalScale = null;
		const linkService = createLinkService();
		scope.scale = scope.scale || null;
		scope.page = attr.page ? parseInt(attr.page, 10) : 1;
		scope.prevPage = scope.page;
		scope.isRendered = false;
		scope.isRendering = false;
		if (goPdfPreviewCtrl) {
			elem.attr('tabindex', '0');
			elem.attr('role', 'document');
			elem.attr('aria-label', 'PDF page content');
		}

		const g2p = new GrabToPan({element: elem[0]});

		scope.zoomIn = function () {
			scope.scale = parseFloat(scope.scale) + 0.1;
			renderPage(scope.page, false);
			g2p.activate();
		};

		scope.zoomOut = function () {
			scope.scale = parseFloat(scope.scale) - 0.1;
			renderPage(scope.page, false);
		};

		scope.fit = function () {
			scope.scale = originalScale;
			renderPage(scope.page, false);
		};

		scope.goto = function (page) {
			if (page > 0 && page <= scope.doc.numPages) {
				scope.prevPage = scope.page;
				scope.page = page;
				changePage();
			}
		};

		scope.next = function () {
			if (scope.page < scope.doc.numPages) {
				scope.prevPage = scope.page;
				scope.page++;
				changePage();
			}
		};

		scope.prev = function () {
			if (scope.page > 1) {
				scope.prevPage = scope.page;
				scope.page--;
				changePage();
			}
		};

		scope.renderPage = renderPage;

		scope.trigger = channel.broadcast.bind(channel);
		scope.on = channel.subscribe.bind(channel);
		scope.off = channel.unsubscribe.bind(channel);
		scope.once = channel.subscribeOnce.bind(channel);

		/**
		 * Change the current page
		 */
		function changePage () {
			scope.trigger('pageChange', {
				page: scope.page,
				numPages: scope.doc.numPages,
				prevPage: scope.prevPage
			});

			// Render the page
			renderPage(scope.page, false).then(function () {
				scope.trigger('pageChanged', {
					page: scope.page,
					numPages: scope.doc.numPages,
					prevPage: scope.prevPage
				});
			});
		}

		/**
		 * Render a single page
		 *
		 * If a scale value is passed, it will override
		 * the current scope scale
		 *
		 * @param pageNum
		 * @param forceResize
		 * @returns {*}
		 */
		let timeoutPromise;
		const pagesRendering = {};
		function renderPage (pageNum, forceResize) {
			scope.scale = forceResize ? null : scope.scale;

			// If the page is already rendering,
			// just return the same promise.
			if (pagesRendering[pageNum]) {
				return pagesRendering[pageNum];
			}
			const renderToken = ++latestRenderToken;

			const deferred = $q.defer(),
				pagePromise = scope.doc.getPage(pageNum);

			pagesRendering[pageNum] = deferred.promise;
			scope.page = pageNum;
			scope.isRendered = false;
			canvasElement = newCanvasInstance();
			resetOverlayLayers();

			// Broadcast page render event
			scope.trigger('pageRender', {
				page: scope.page,
				numPages: scope.doc.numPages,
				prevPage: scope.prevPage
			});

			// Don't broadcast a rendering event
			// if it takes less than a second to render.
			$timeout.cancel(timeoutPromise);
			timeoutPromise = $timeout(function () {
				// If it is not rendered yet,
				// then we need to broadcast
				// a page rendering event
				if (!scope.isRendered) {
					scope.isRendering = true;
					scope.trigger('pageRendering', {
						page: scope.page,
						numPages: scope.doc.numPages,
						prevPage: scope.prevPage
					});
				}
			}, RENDERING_THRESHOLD);

			// Wait for page to load
			pagePromise.then(function (page) {
				const ctx = canvasElement[0].getContext('2d');

				// scale being null means we are rendering for the first time
				// and need to pick the scale that matches the element size
				let viewport;
				if (!scope.scale) {
					const elemWidth = elem[0].offsetWidth;
					const elemHeight = elem[0].offsetHeight;
					viewport = page.getViewport({ scale: 1 });
					scope.scale = Math.min(elemWidth / viewport.width, elemHeight / viewport.height);
				}

				if (scope.scale < 0) {
					scope.scale = 0.01;
				}

				viewport = page.getViewport({ scale: scope.scale });

				setCanvasDimensions(canvasElement[0], viewport.width, viewport.height);
				setElementDimensions(pageContentElement, viewport.width, viewport.height);
				if (textLayerElement) {
					setElementDimensions(textLayerElement, viewport.width, viewport.height);
					textLayerElement.style.setProperty('--total-scale-factor', String(scope.scale));
				}
				if (annotationLayerElement) {
					setElementDimensions(annotationLayerElement, viewport.width, viewport.height);
					annotationLayerElement.style.setProperty('--total-scale-factor', String(scope.scale));
				}

				if (!originalScale) {
					originalScale = scope.scale;
				}

				page.render({
					canvasContext: ctx,
					viewport
				}).promise.then(function () {
					renderLayers(page, viewport, renderToken);
					deferred.resolve();
				});
			});

			deferred.promise.then(function () {
				// Set as rendered and not rendering
				scope.isRendered = true;
				scope.isRendering = false;
				delete pagesRendering[pageNum];

				scope.onPageRender({
					page: scope.page,
					numPages: scope.doc.numPages,
					prevPage: scope.prevPage
				});

				// Broadcast page rendered event
				scope.trigger('pageRendered', {
					page: scope.page,
					numPages: scope.doc.numPages,
					prevPage: scope.prevPage
				});
			});

			return deferred.promise;
		}

		/**
		 * Create a new canvas instance
		 *
		 * @returns {*}
		 */
		function newCanvasInstance () {
			let instance = angular.element(pageContentElement.querySelector('canvas'));

			if (!instance.length) {
				instance = angular.element('<canvas class="go-pdf-canvas"></canvas>');
				pageContentElement.appendChild(instance[0]);
			}

			return instance;
		}

		function resetOverlayLayers () {
			if (textLayerElement) {
				textLayerElement.innerHTML = '';
				const endOfContent = document.createElement('div');
				endOfContent.className = 'endOfContent';
				textLayerElement.appendChild(endOfContent);
			}

			if (annotationLayerElement) {
				annotationLayerElement.innerHTML = '';
			}
		}

		function renderLayers (page, viewport, renderToken) {
			return $q.when(pdfJsPromise).then(function (pdfjs) {
				if (pdfjs == null || renderToken !== latestRenderToken) {
					return;
				}

				return $q.all([
					renderTextLayer(pdfjs, page, viewport, renderToken),
					renderAnnotationLayer(pdfjs, page, viewport, renderToken)
				]).then(function () {
					return renderImageAltTextLayer(page, viewport, renderToken);
				});
			}).catch(angular.noop);
		}

		function renderTextLayer (pdfjs, page, viewport, renderToken) {
			if (textLayerElement == null || renderToken !== latestRenderToken) {
				return;
			}

			const textContentSource = typeof page.streamTextContent === 'function'
				? page.streamTextContent({ includeMarkedContent: true })
				: page.getTextContent({ includeMarkedContent: true });

			return $q.when(textContentSource).then(function (textContent) {
				if (renderToken !== latestRenderToken) {
					return;
				}

				const textLayer = new pdfjs.TextLayer({
					textContentSource: textContent,
					container: textLayerElement,
					viewport
				});
				return textLayer.render();
			});
		}

		function renderAnnotationLayer (pdfjs, page, viewport, renderToken) {
			if (annotationLayerElement == null || renderToken !== latestRenderToken) {
				return;
			}

			const annotationStorage = scope.doc == null ? undefined : scope.doc.annotationStorage;
			const annotationViewport = typeof viewport.clone === 'function'
				? viewport.clone({ dontFlip: true })
				: viewport;

			return $q.when(page.getAnnotations({ intent: 'display' })).then(function (annotations) {
				if (renderToken !== latestRenderToken) {
					return;
				}

				const annotationLayer = new pdfjs.AnnotationLayer({
					div: annotationLayerElement,
					page,
					viewport: annotationViewport,
					linkService,
					annotationStorage
				});

				return annotationLayer.render({
					viewport: annotationViewport,
					div: annotationLayerElement,
					annotations,
					page,
					linkService,
					annotationStorage,
					renderForms: true
				});
			});
		}

		function renderImageAltTextLayer (page, viewport, renderToken) {
			if (textLayerElement == null || renderToken !== latestRenderToken || typeof page.getStructTree !== 'function') {
				return;
			}

			return $q.when(page.getStructTree()).then(function (structTree) {
				if (renderToken !== latestRenderToken || structTree == null) {
					return;
				}

				const figures = getFigureAltTextNodes(structTree);
				figures.forEach(function (figure) {
					appendFigureAltText(figure, viewport);
				});
			}).catch(angular.noop);
		}

		function getFigureAltTextNodes (node) {
			const figures = [];

			const walk = function (currentNode) {
				if (currentNode == null || typeof currentNode !== 'object') {
					return;
				}

				const children = Array.isArray(currentNode.children) ? currentNode.children : [];
				const isFigure = currentNode.role === 'Figure';
				const hasAlt = typeof currentNode.alt === 'string' && currentNode.alt !== '';
				const contentNode = children.find(function (child) {
					return child != null
						&& child.type === 'content'
						&& typeof child.id === 'string'
						&& child.id !== '';
				});

				if (
					isFigure
					&& hasAlt
				) {
					figures.push({
						alt: currentNode.alt,
						bbox: Array.isArray(currentNode.bbox) && currentNode.bbox.length === 4
							? currentNode.bbox
							: null,
						id: contentNode == null ? null : contentNode.id
					});
				}

				children.forEach(function (child) {
					walk(child);
				});
			};

			walk(node);
			return figures;
		}

		function appendFigureAltText (figure, viewport) {
			let parentElement: HTMLElement = textLayerElement;
			if (typeof figure.id === 'string' && figure.id !== '') {
				const contentElement = document.getElementById(figure.id);
				if (contentElement != null && textLayerElement.contains(contentElement)) {
					parentElement = contentElement as HTMLElement;
				}
			}

			const pageBounds = viewport.viewBox || [0, 0, viewport.width, viewport.height];
			const pageX = pageBounds[0];
			const pageY = pageBounds[1];
			const pageHeight = pageBounds[3] - pageBounds[1];
			const scale = typeof viewport.scale === 'number' ? viewport.scale : 1;

			const altTextElement = document.createElement('span');
			altTextElement.className = 'go-pdf-image-alt-text';
			altTextElement.setAttribute('role', 'img');
			altTextElement.setAttribute('aria-label', sanitizeAriaText(figure.alt));
			if (Array.isArray(figure.bbox) && figure.bbox.length === 4) {
				altTextElement.style.width = `${(figure.bbox[2] - figure.bbox[0]) * scale}px`;
				altTextElement.style.height = `${(figure.bbox[3] - figure.bbox[1]) * scale}px`;
				altTextElement.style.left = `${(figure.bbox[0] - pageX) * scale}px`;
				altTextElement.style.top = `${(pageHeight - figure.bbox[3] + pageY) * scale}px`;
			} else {
				altTextElement.style.width = '1px';
				altTextElement.style.height = '1px';
				altTextElement.style.left = '0px';
				altTextElement.style.top = '0px';
			}
			parentElement.appendChild(altTextElement);
		}

		function sanitizeAriaText (value) {
			return value.replace(/\u0000/g, '');
		}

		function createLinkService () {
			function getAnchorUrl (anchor) {
				return anchor ? '#' + anchor : '#';
			}

			return {
				addLinkAttributes (link, url, newWindow) {
					if (url == null || url === '') {
						link.href = getAnchorUrl('');
						return;
					}

					link.href = url;
					link.title = url;
					link.target = newWindow ? '_blank' : '_self';
					link.rel = 'noopener noreferrer nofollow';
				},
				getDestinationHash (destination) {
					if (typeof destination === 'string') {
						return getAnchorUrl(destination);
					}
					return getAnchorUrl('');
				},
				getAnchorUrl,
				goToDestination (destination) {
					navigateToDestination(destination);
				},
				goToPage (value) {
					scope.$evalAsync(function () {
						scope.goto(value);
					});
				},
				executeNamedAction: angular.noop,
				executeSetOCGState: angular.noop,
				eventBus: null,
				externalLinkTarget: 0,
				externalLinkRel: 'noopener noreferrer nofollow',
				externalLinkEnabled: true
			};
		}

		function navigateToDestination (destination) {
			if (typeof destination === 'string' && scope.doc && typeof scope.doc.getDestination === 'function') {
				$q.when(scope.doc.getDestination(destination)).then(navigateToDestination).catch(angular.noop);
				return;
			}

			if (Array.isArray(destination) && destination.length > 0) {
				const target = destination[0];

				if (typeof target === 'number') {
					scope.$evalAsync(function () {
						scope.goto(target + 1);
					});
					return;
				}

				if (target != null && scope.doc && typeof scope.doc.getPageIndex === 'function') {
					$q.when(scope.doc.getPageIndex(target)).then(function (pageIndex) {
						scope.$evalAsync(function () {
							scope.goto(pageIndex + 1);
						});
					}).catch(angular.noop);
				}
			}
		}

		// expose me to my parent if defined
		if (goPdfPreviewCtrl) {
			goPdfPreviewCtrl.register(scope);
		}

		// Load document
		let promise;
		if (scope.doc) {
			promise = $q.when(scope.doc);
		} else {
			promise = goPdfLoader.load(scope.url);
		}

		// Wait for document to load
		// and then render the first page.
		promise.then((doc) => {
			scope.doc = doc;
			scope.onLoad({document: scope});
			scope.$evalAsync();

			renderPage(scope.page, false).then(() => {
				elem.removeClass('loading');
			});
		});

		elem.addClass('loading');
	}

	return {
		scope: isolateScope,
		link,
		require: '^?goDocViewer',
		template
	};
}
